import { KvaPeriod, ProposalPayload } from "../../types/proposalDocuments.types";

export type FuelType = "electricity" | "gas";

export interface RateRow {
  label: string;
  estimatedAnnualCost: number;
  defaultRate: string;
  hidden: boolean;
  fuelTypes: FuelType[];
  // Non-commodity (pass-through) charges don't have their own consumption -
  // theirs is pegged to the total consumption of the commodity rates in the
  // same quote instead of being entered directly. See rateRow.tsx /
  // supplierQuoteCard.tsx for where this is applied.
  nonCommodity: boolean;
}

export interface SLAOption {
  id: string;
  label: string;
}

export interface RateUnit {
  id: string;
  label: string;
  currency: string;
  hasConsumption: boolean;
  // Only the KVA capacity unit needs a daily/monthly/annually period
  // selector - see `KvaPeriod` and `KVA_PERIOD_MULTIPLIERS`.
  hasPeriodSelector: boolean;
  transform: (consumption: number, rate: number, periodMultiplier?: number) => number;
}

export const KVA_PERIOD_MULTIPLIERS: Record<KvaPeriod, number> = {
  daily: 365,
  monthly: 12,
  annually: 1,
};

export const KVA_PERIOD_OPTIONS: { id: KvaPeriod; label: string }[] = [
  { id: "daily", label: "Daily" },
  { id: "monthly", label: "Monthly" },
  { id: "annually", label: "Annually" },
];

export const SLABenefitOptions: SLAOption[] = [{ id: "default", label: "Default" }];

export const SLACalloutOptions: SLAOption[] = [{ id: "default", label: "Default" }];

export const AVAILABLE_RATES: RateRow[] = [
  {
    label: "Daily Standing Charge",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_day",
    fuelTypes: ["electricity", "gas"],
    nonCommodity: false,
  },
  {
    label: "24 Hour Unit Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Day Unit Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Night Unit Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Summer Day Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Summer Night Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Winter Day Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Winter Night Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "Unit Rate",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity", "gas"],
    nonCommodity: false,
  },
  {
    label: "KVA",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "gbp_per_kva_day",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "BSUoS",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
  {
    label: "CM",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "gbp_per_day",
    fuelTypes: ["electricity"],
    nonCommodity: false,
  },
  {
    label: "DUoS Fixed",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
  {
    label: "FIT",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
  {
    label: "CFD",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
  {
    label: "RO",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
  {
    label: "TNUoS Fixed",
    hidden: false,
    estimatedAnnualCost: 0,
    defaultRate: "p_per_kwh",
    fuelTypes: ["electricity"],
    nonCommodity: true,
  },
];

export const AVAILABLE_UNITS: RateUnit[] = [
  {
    id: "p_per_kwh",
    label: "p/kWh",
    currency: "p",
    hasConsumption: true,
    hasPeriodSelector: false,
    transform: (consumption, rate) => (consumption * rate) / 100,
  },
  {
    id: "gbp_per_kva_day",
    label: "£/KVA/day",
    currency: "£",
    hasConsumption: true,
    hasPeriodSelector: true,
    transform: (consumption, rate, periodMultiplier = 365) => consumption * rate * periodMultiplier,
  },
  {
    id: "p_per_day",
    label: "p/day",
    currency: "p",
    hasConsumption: false,
    hasPeriodSelector: false,
    transform: (_consumption, rate) => (rate * 365) / 100,
  },
  {
    id: "gbp_per_day",
    label: "£/day",
    currency: "£",
    hasConsumption: false,
    hasPeriodSelector: false,
    transform: (_consumption, rate) => rate * 365,
  },
];

export const getUnitByLabel = (label: string): RateUnit | undefined =>
  AVAILABLE_UNITS.find((u) => u.label === label);

// The unit that represents actual kWh commodity usage - KVA's consumption
// is a kVA capacity figure, not kWh, so it's deliberately excluded from
// "commodity consumption" totals even though it has its own consumption
// field (see recalculateNonCommodityRates in supplierQuoteCard.tsx).
const COMMODITY_UNIT_ID = "p_per_kwh";

export const isCommodityConsumptionRate = (rate: { nonCommodity: boolean; unit: string }) =>
  !rate.nonCommodity && getUnitByLabel(rate.unit)?.id === COMMODITY_UNIT_ID;

export const computeRateCost = (
  consumption: number,
  value: number,
  unitLabel: string,
  periodMultiplier?: number,
) => {
  const unit = getUnitByLabel(unitLabel);
  return unit ? unit.transform(consumption, value, periodMultiplier) : 0;
};

// A base proposal payload for state
export const BASE_PAYLOAD: ProposalPayload = {
  format: "html",
  zoho_id: "",
  use_print_friendly_colours: false,
  customer_name: "",
  customer_image: null,
  theme: "base",
  who_we_are_blurb:
    "Since 2015, The Green Energy Advice Bureau (GEAB) has evolved to become one of the UK’s leading energy procurement consultancies.",
  who_we_are_paragraphs: [
    "GEAB’s growing team of award-winning energy consultants provide efficiency analysis to save our customers time and money.",
    "We will review your business requirements against your current costs and annual expenditure, before navigating the energy market to find the best solutions available to your business.",
    "Our energy experts work side-by-side with your business, offering professional expertise and exclusive insights to support your aims and facilitate green objects.",
    "We assist customers in implementing a wide range of energy-saving strategies through an established commercial support network.",
    "We’re proud to say that The Green Energy Advice Bureau contributes to a greener, more sustainable future and the ongoing success of the portfolio of businesses we support across every sector.",
  ],
  testimonials: [
    {
      quote:
        "Exceptional Support, Honest Advice, and Great Energy Savings. Michael Hilton and Robert Bulmer used their knowledge, experience, and guidance to help us successfully exit an energy contract that had been mis-sold to us. Once resolved, they helped us secure a genuinely fully fixed electricity contract at competitive market rates. Professional, knowledgeable, and trustworthy service from start to finish.",
      author: "SM",
      company: "Youngs Foods Limited",
    },
    {
      quote:
        "Karen from GEAB took the time to genuinely look into the detail. She identified discrepancies, explained technical points in plain English, and helped me understand what should have been happening versus what actually was. What really stands out about GEAB is that this wasn't a sales push — it was proper, independent advice tailored to our situation. Calm, knowledgeable, and genuinely helpful.",
      author: "Gillian J.",
      company: "TBC",
    },
  ],
  quote_sections: [],
  layout: "supplier",
  pdf_quality: "ebook",
  show_sla_content: false,
  sla_benefits: "default",
  sla_callout: "default",
};
